import generateMediaQuery from '../../image/utils/generateMediaQuery';
import type { Device, ImageVariantsDesktopOnly, ImageVariantsMobileOnly, ImageNode, ImageVariants } from 'data-layer';

/**
 * Generates metadata for an image based on device type and media settings.
 *
 * This function provides metadata such as the image source (`src`), alternative text (`alt`),
 * dimensions (`width` and `height`), and responsive sizes based on whether the view is for desktop
 * or mobile and whether a video is present.
 *
 * It handles fallbacks by using the mobile image's `alt` and `title` if `isMobile` is true, or
 * falling back to the desktop image's values if missing.
 *
 * @param images - The object containing image URLs for different device sizes.
 * @param device - The device type, either 'mobile' or 'desktop'.
 * @param image - The image node containing `alt` and `title`.
 * @returns An object containing image metadata:
 *  - `alt`: The alternative text for the image.
 *  - `sizes`: The responsive sizes attribute for the image.
 *  - `src`: The appropriate image URL based on the device type and video presence.
 *  - `title`: The title of the image.
 *  - `width`: The width of the image based on the device type.
 *  - `height`: The height of the image based on the device type.
 */
function getImageMetaData(imageVariantsByDeviceSize: ImageVariants, device: Device, image?: ImageNode) {
  const imageVariantByDeviceSize =
    device === 'mobile'
      ? (imageVariantsByDeviceSize as ImageVariantsMobileOnly).phoneImage
      : (imageVariantsByDeviceSize as ImageVariantsDesktopOnly).desktopImage;

  return {
    alt: image?.alt ?? '',
    title: image?.title ?? '',
    src: imageVariantByDeviceSize['1x'],
    width: imageVariantByDeviceSize.width,
    height: imageVariantByDeviceSize.height,
    sizes: generateMediaQuery(960, '65vw', '100vw'),
  };
}

/**
 * Checks if an image has shoppable hotspots.
 *
 * @param image - The image node to check
 * @returns True if the image has one or more shoppable hotspots
 *
 * @example
 * const hasHotspots = hasShoppableHotspots(image);
 * // Returns: true if image.shopableHotspots.items has length > 0
 */
function hasShoppableHotspots(image?: ImageNode): boolean {
  return Boolean(image?.shopableHotspots?.items?.length);
}

export { getImageMetaData, hasShoppableHotspots };
